import { useApi } from '@/api';
import { Asset, Page, PageSkeleton } from '@/components';
import { useSearchParams } from '@/hooks';
import {
  Card,
  DisplaySwitch,
  EmptyBox,
  Flex,
  TextSearchFilter,
  useTableProps,
  SearchIcon
} from '@blockanalitica/ui';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import MarketWalletsBorrowersTable from './MarketWalletsBorrowersTable';
import MarketWalletsSuppliersTable from './MarketWalletsSuppliersTable';
import { DEFAULT_NETWORK } from '@/constants';

type MarketResponse = {
  underlying_symbol: string;
  underlying_name: string;
  status: {
    borrow: string;
    collateral: string;
    supply: string;
    frozen: string;
  };
};

export default function MarketWalletsPage() {
  const { marketAddress } = useParams() as {
    marketAddress: string;
  };
  const network = DEFAULT_NETWORK;
  const [wallets, setWalletsParam] = useSearchParams('type', 'suppliers');

  const displayOptions = useMemo(
    () => [
      { key: 'suppliers', value: 'Suppliers' },
      { key: 'borrowers', value: 'Borrowers' }
    ],
    []
  );
  const handleDisplayChange = useCallback(
    (newValue: string) => {
      setWalletsParam(newValue);
    },
    [setWalletsParam]
  );

  const { pagination, filters } = useTableProps({
    pageSize: 10,
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: ''
    }
  });

  const [data, error] = useApi<MarketResponse>({
    url: `/markets/${marketAddress}/`
  });

  if (error) {
    throw error;
  }

  if (data) {
    let content;
    if (_.isEmpty(data)) {
      content = <EmptyBox />;
    } else if (wallets === 'suppliers') {
      content = (
        <MarketWalletsSuppliersTable
          marketAddress={marketAddress}
          network={network}
          pagination={pagination}
          textSearch={filters.filtersApplied.textSearch}
        />
      );
    } else {
      content = (
        <MarketWalletsBorrowersTable
          marketAddress={marketAddress}
          network={network}
          pagination={pagination}
          textSearch={filters.filtersApplied.textSearch}
          tag={data.status['borrow']}
          symbol={data.underlying_symbol}
        />
      );
    }

    return (
      <Page
        title={
          <Asset
            symbol={data.underlying_symbol}
            address={marketAddress}
            network={network}
            iconSize="4"
            fontSize="5"
            suffix="Wallets"
          />
        }>
        <Card variant="hypeCard">
          <Flex flexDirection="row" justifyContent="space-between" padding="xs">
            <DisplaySwitch
              data={displayOptions}
              activeOption={wallets}
              onChange={handleDisplayChange}
            />
            <TextSearchFilter
              placeholder="Search by wallet"
              pagination={pagination}
              prefix=<SearchIcon size="0" />
              {...filters}
            />
          </Flex>

          {content}
        </Card>
      </Page>
    );
  }

  return <PageSkeleton />;
}
